import { useState } from 'react';
import { Show, useUser, useClerk } from '@clerk/react';
import { Link } from 'wouter';
import {
  useListComments,
  useCreateComment,
  useDeleteComment,
  getListCommentsQueryKey,
} from '@workspace/api-client-react';
import { useQueryClient } from '@tanstack/react-query';

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function Comments({ articleSlug }: { articleSlug: string }) {
  const queryClient = useQueryClient();
  const queryKey = getListCommentsQueryKey({ articleSlug });
  const { data: comments, isLoading } = useListComments({ articleSlug });
  const createComment = useCreateComment();
  const deleteComment = useDeleteComment();
  const { user } = useUser();
  const [body, setBody] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = body.trim();
    if (!trimmed) return;
    createComment.mutate(
      { data: { articleSlug, body: trimmed } },
      {
        onSuccess: () => {
          setBody('');
          queryClient.invalidateQueries({ queryKey });
        },
      },
    );
  };

  const handleDelete = (id: number) => {
    deleteComment.mutate(
      { id },
      { onSuccess: () => queryClient.invalidateQueries({ queryKey }) },
    );
  };

  return (
    <section className="mt-16 pt-8 border-t border-border" aria-label="Comments">
      <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-6">
        Discussion {comments && comments.length > 0 ? `(${comments.length})` : ''}
      </h2>

      <Show when="signed-in">
        <form onSubmit={handleSubmit} className="mb-8">
          <label htmlFor="comment-body" className="sr-only">
            Add a comment
          </label>
          <textarea
            id="comment-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={`Share your thoughts, ${user?.firstName ?? 'friend'}...`}
            rows={3}
            maxLength={2000}
            className="w-full rounded-xl border border-border bg-card p-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
          />
          <div className="flex justify-end mt-2">
            <button
              type="submit"
              disabled={createComment.isPending || !body.trim()}
              className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createComment.isPending ? 'Posting…' : 'Post comment'}
            </button>
          </div>
        </form>
      </Show>

      <Show when="signed-out">
        <div className="mb-8 p-4 rounded-xl border border-dashed border-border text-sm text-muted-foreground">
          <Link href="/sign-in" className="text-primary font-medium hover:underline">
            Sign in
          </Link>{' '}
          to join the discussion and leave a comment.
        </div>
      </Show>

      {isLoading && (
        <p className="text-sm text-muted-foreground">Loading comments…</p>
      )}

      {!isLoading && comments && comments.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No comments yet — be the first to share your thoughts.
        </p>
      )}

      <ul className="space-y-5">
        {comments?.map((comment) => (
          <li key={comment.id} className="rounded-xl border border-border p-4">
            <div className="flex items-center justify-between gap-3 mb-1.5">
              <span className="text-sm font-serif text-foreground">
                {comment.authorName}
              </span>
              <div className="flex items-center gap-3">
                <time className="text-xs text-muted-foreground">
                  {formatDate(comment.createdAt)}
                </time>
                {comment.canDelete && (
                  <button
                    type="button"
                    onClick={() => handleDelete(comment.id)}
                    disabled={deleteComment.isPending}
                    className="text-xs text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
            <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap">
              {comment.body}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}
